import { useCallback, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import { useDatatableContext } from "@/components/tables/contexts/DatatableContextProvider";
import { useTablePagination } from "@/components/tables/hooks/useTablePagination";
import { parseSortDirection, type SortDirection } from "@/components/tables/types";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import { formatDateString } from "./utils";

/**
 * Server-side sortable columns for ``/databricks/queries/unified``.
 * Mirrors the backend ``valid_sort_columns`` whitelist; any other
 * value (typo, stale bookmark, manual URL edit) is normalised back
 * to the default below so the FE doesn't quietly send a column the
 * BE will discard.
 */
export const QUERY_SORT_FIELDS = [
  "start_time",
  "total_duration_ms",
  "estimated_cost_usd",
  "spilled_local_bytes",
  "statement_type",
  // ``warehouse_name``, ``workspace_name``, ``read_bytes`` and
  // ``user_name`` were removed from the whitelist. The first two
  // would require a full inventory JOIN before LIMIT (negating the
  // two-phase win), and the latter two moved off the table. A stale
  // bookmark like ``?sort_by=warehouse_name`` now normalises back to
  // ``DEFAULT_QUERIES_SORT`` instead of producing a hidden sort the
  // user can't see or undo.
] as const;

export type QuerySortField = (typeof QUERY_SORT_FIELDS)[number];

const QUERY_SORT_FIELD_SET: ReadonlySet<string> = new Set(QUERY_SORT_FIELDS);

const isValidQuerySortField = (value: string | null): value is QuerySortField =>
  value !== null && QUERY_SORT_FIELD_SET.has(value);

/**
 * Single source of truth for the Queries page's default sort.
 * Referenced by the filters hook (URL fallback), the DataTable
 * (``defaultSort`` prop), and the reset-to-default branch of the
 * sort writer. Hoisted so changing the default touches one place.
 *
 * ``total_duration_ms desc`` is the BE-fast-path default — sorting
 * by a ``dbx_query_history`` column lets the BE page the history
 * table directly before any JOIN (cost-sort defaults push the BE
 * into a slower path even with the cost-first optimisation).
 */
export const DEFAULT_QUERIES_SORT = {
  sortParamValue: "total_duration_ms",
  order: "desc",
} as const satisfies { sortParamValue: QuerySortField; order: "asc" | "desc" };

/**
 * Filters hook for the Databricks Queries page.
 * Reads filter state from the DatatableContext (More Filters sheet) and
 * derives all params needed by the queries APIs.
 */
export function useDatabricksQueriesFilters() {
  const { getFilterValue, isInitialized } = useDatatableContext();
  const { page, pageSize } = useTablePagination();
  const { searchParams, updateSearchParamsByKey } = useFiltersBySearchParams();

  // Date range still lives in URL params (driven by the page header DateRange).
  const { startDate, endDate, onUpdate } = useDateFromSearchParams(
    dayjs().subtract(28, "day").toDate(),
    dayjs().toDate()
  );

  // API expects date-only strings (YYYY-MM-DD); ISO with time is rejected.
  const dateRange = useMemo(
    () => ({
      start_date: formatDateString(startDate),
      end_date: formatDateString(endDate || new Date()),
    }),
    [startDate, endDate]
  );

  const workspaceIdFromUrl = useMemo(
    () => searchParams.get("workspace") || undefined,
    [searchParams]
  );

  // Single-select filters from the More Filters sheet (DatatableContext
  // stores DROPDOWN_SINGLE as a plain string, synced from URL).
  const statementTypeFilter = useMemo(
    () => getFilterValue<string>("statementType") || "",
    [getFilterValue]
  );
  // Multi-select column filters live as repeated URL params (e.g.
  // ``?warehouseId=A&warehouseId=B``). Query ID is a server-searched
  // API dropdown — selected IDs flow the same way.
  //
  // ``userName`` and ``workspaceId`` are read DIRECTLY from URL params
  // (not via the context registry's ``getFilterValue``). Their column
  // headers were removed, so the More-Filters drawer surfaces them via
  // ``useRegisterDatabricksQueryFilters`` instead. ``searchParams.getAll``
  // keeps the legacy single-value drilldown URLs (``?userName=alice``,
  // ``?workspace=ws-1``) working — the one-element array degrades back
  // to the same BE-side ``IN ('alice')`` filter shape.
  const userNameFilter = useMemo(() => searchParams.getAll("userName"), [searchParams]);
  const statementIdFilter = useMemo(() => searchParams.getAll("queryId"), [searchParams]);
  const warehouseIdFilter = useMemo(() => searchParams.getAll("warehouseId"), [searchParams]);
  // Unified Compute dropdown — values are prefixed ``{kind}:{id}`` from
  // ``ComputeFilterOption`` so the BE can OR warehouse + cluster +
  // pipeline matches without overlapping the legacy ``warehouseId`` key.
  const computeFilter = useMemo(() => searchParams.getAll("compute"), [searchParams]);
  const workspaceIdFilter = useMemo(() => searchParams.getAll("workspaceId"), [searchParams]);

  // Column filter takes precedence over the URL-driven workspace pre-selection.
  const workspaceId = workspaceIdFilter.length > 0 ? undefined : workspaceIdFromUrl;

  // Sort still lives in URL params for now. Normalise against the
  // server-side whitelist so a bookmark/typo with an unsupported column
  // (e.g. ``?sort_by=statement_id``) falls back to the default — without
  // this, the FE renders a stale header indicator while the BE silently
  // sorts by ``estimated_cost_usd``. When ``sort_by`` is invalid we ALSO
  // fall back to the default ``sort_order`` so a bookmark like
  // ``?sort_by=user_name&sort_order=asc`` doesn't emit
  // ``sort_by=total_duration_ms&sort_order=asc`` — still not the
  // canonical default and still without a visible sort indicator.
  const sortAttr: QuerySortField = useMemo(() => {
    const fromUrl = searchParams.get("sort_by");
    return isValidQuerySortField(fromUrl) ? fromUrl : DEFAULT_QUERIES_SORT.sortParamValue;
  }, [searchParams]);
  const sortOrder = useMemo(
    () =>
      isValidQuerySortField(searchParams.get("sort_by"))
        ? parseSortDirection(searchParams.get("sort_order"), DEFAULT_QUERIES_SORT.order)
        : DEFAULT_QUERIES_SORT.order,
    [searchParams]
  );

  // Write the default sort to the URL on first load when ``sort_by``
  // is absent (or set to a non-whitelisted value). Makes the URL
  // canonical immediately — refresh/share-link/back navigation all
  // round-trip to the same sort the page is actually rendering,
  // instead of "no params" → "default applied implicitly" → "user
  // clicks anything → params appear" sequence.
  useEffect(() => {
    if (!isValidQuerySortField(searchParams.get("sort_by"))) {
      updateSearchParamsByKey({
        sort_by: DEFAULT_QUERIES_SORT.sortParamValue,
        sort_order: DEFAULT_QUERIES_SORT.order,
      });
    }
    // ``searchParams`` is the only signal — once we write the default
    // it's no longer absent, so the effect won't loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Centralized sort writer — keeps the URL contract for sort in
  // ONE place. The DataTable's column header was calling
  // ``updateSearchParamsByKey`` directly, which split read + write
  // across two non-obvious sites. ``direction === ""`` (Tri-state
  // unset) resets to the canonical default rather than leaving the
  // URL in a "no sort" state the BE doesn't really support.
  const setSort = useCallback(
    (attribute: string, direction: SortDirection) => {
      if (direction === "") {
        updateSearchParamsByKey({
          sort_by: DEFAULT_QUERIES_SORT.sortParamValue,
          sort_order: DEFAULT_QUERIES_SORT.order,
          page: "1",
        });
      } else {
        updateSearchParamsByKey({ sort_by: attribute, sort_order: direction, page: "1" });
      }
    },
    [updateSearchParamsByKey]
  );

  return {
    isInitialized,
    page,
    pageSize,
    startDate,
    endDate,
    onDateUpdate: onUpdate,
    dateRange,
    workspaceId,
    statementTypeFilter,
    userNameFilter,
    statementIdFilter,
    warehouseIdFilter,
    computeFilter,
    workspaceIdFilter,
    sortAttr,
    sortOrder,
    setSort,
  };
}
